<template>
  <dashboard-widget
    :is-loading="isLoading"
    is-panel
  >
    <template>
      <div class="latest-activity">
        <div v-if="activities.length > 0 && !isError">
            <div class="widget-header">
                <h4>{{ $t('ui.dashboard.latest_activity.label') }}</h4>
            </div>

            <div class="list">
                <div v-for="(activity, index) in activities" :key="index" class="activity-item">
                  <div class="icon">
                      <img v-if="activity.activity === LEAD_CREATE" src="../../../../../assets/latest-activity-created.svg" />
                      <img v-else-if="activity.activity === LEAD_UPDATE" src="../../../../../assets/latest-activity-list-update.svg" />
                  </div>

                  <div class="content">
                      <router-link
                        class="link"
                        :to="{
                          name: 'project_lead_edit',
                          params: {
                            id: activity.project.identifier,
                            lead_id: activity.lead.identifier
                          }
                        }">
                        <div v-if="activity.person.name">
                          {{ activity.person.name }}
                        </div>
                        <div v-else>
                          {{ $t('ui.dashboard.latest_activity.incomplete_lead') }}
                        </div>
                      </router-link>
                      <p v-if="activity.activity === LEAD_CREATE" class="activity-description truncate-text link">
                        {{ $t('ui.dashboard.latest_activity.added_to') }} <router-link :to="{ name: 'project_leads', params: { id: activity.project.identifier } }">
                          {{ activity.project.name }}
                        </router-link>
                      </p>
                      <p v-else-if="activity.activity === LEAD_UPDATE" class="activity-description truncate-text link">
                        {{ $t('ui.dashboard.latest_activity.edited_in') }} <router-link :to="{ name: 'project_leads', params: { id: activity.project.identifier } }">
                          {{ activity.project.name }}
                        </router-link>
                      </p>
                      <p class="activity-details caption" v-if="activity.by !== void 0">
                        {{ $t('ui.dashboard.latest_activity.by') }} <span>{{ activity.by }}</span> - {{ activity.at | date }}
                      </p>
                      <p class="activity-details caption" v-else>
                        {{ activity.at | date }}
                      </p>
                  </div>
                </div>
            </div>
        </div>

        <div
          v-else-if="activities.length === 0 && !isError"
          class="aligner">
          <div class="text-center empty-state">
            <img src="../../../../../assets/empty-default.svg">
            <p class="empty-state-margin">
              {{ $t('ui.dashboard.activity_list_widget.empty_state_headline') }}
            </p>
            <router-link
              class="link"
              :to="{ name: 'project_new' }"
            >
              {{ $t('ui.dashboard.activity_list_widget.empty_state_intro') }}
            </router-link>
          </div>
        </div>

        <div v-if="isError"
        class="aligner">
          <div class="text-center empty-state">
            <img src="../../../../../assets/empty-default.svg">
            <p class="empty-state-margin">
              {{ $t('ui.dashboard.widget_error') }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </dashboard-widget>
</template>


<script>
import map from 'lodash/fp/map'
import reject from 'lodash/fp/reject'
import flow from 'lodash/flow'
import cond from 'lodash/cond'
import isNull from 'lodash/isNull'
import slice from 'lodash/fp/slice'
import get from 'lodash/get'
import join from 'lodash/join'
import find from 'lodash/find'
import eq from 'lodash/eq'
import size from 'lodash/size'
import isUndefined from 'lodash/isUndefined'
import stubTrue from 'lodash/stubTrue'
import constant from 'lodash/constant'

import Charts from '@/api/Charts.js'
import Api from '@/api/Api.js'
import ProjectApi from '@/api/Project.js'
import WidgetMixin from './mixins/WidgetMixin'
import WidgetPeriodMixin from './mixins/WidgetPeriodMixin'
import WidgetDateMixin from './mixins/WidgetDateMixin'
import WidgetQueryMixin from './mixins/WidgetQueryMixin'
import { getLeadName } from '../../services/DashboardService.js'

const ACTIVITIES_LIMIT = 10
const LEAD_CREATE = 'LEAD_CREATE'
const LEAD_UPDATE = 'LEAD_UPDATE'

export default {
  mixins: [WidgetMixin, WidgetPeriodMixin, WidgetQueryMixin, WidgetDateMixin],

  props: {
    identifier: {
      type: String,
      default: 'latest-activity'
    }
  },

  data: () => ({
    activities: [],
    LEAD_CREATE,
    LEAD_UPDATE
  }),

  methods: {
    async refresh() {
        this.setIsLoading(true)

        try {
            let activities = await this.get(this.from(this.filter.period), this.filter.projectId)
            this.activities = this.transform(activities)
        } catch (error) {
            this.setError(error)
            this.setIsError(true)
        } finally {
            this.setIsLoading(false)
            this.eventBus.$emit('widget-loaded', this.identifier)
        }
    },
    async get (from, project) {
      return new Promise(async (resolve, reject) => {
        let charts = new Charts()

        // Temporary query more events for the case if some are archived
        // or doesn't match the actual leads
        const margin = 5

        let tuples = [
          { key: 'from', value: `"${from}"` },
          { key: 'limit', value: ACTIVITIES_LIMIT + margin },
          { key: 'eventNames', value: '[LEAD_CREATE, LEAD_UPDATE]' }
        ]

        if (!!project) {
          tuples.push({ key: 'projectId', value: project })
        }

        const querySchema = `
          name
          happened_at
          project_id
          lead_id
          user_id
        `
        const query = this.getQuery(tuples, 'latestEvents', querySchema)
        let { latest_events: latestActivities } = await charts.postQuery({
          organization_id: this.filter.organizationId,
          operationName: null,
          variables: {},
          query: `query Query {
            ${query}
          }`
        })
      .catch(reject)


        // If no events returned, no need for querying API
        if (eq(size(latestActivities), 0)) {
          resolve([])

          return
        }

        let api = new Api()
        let projectApi = new ProjectApi()

        const userIds = join(map(({ user_id }) => user_id)(latestActivities))
        const usersPromise = api.getMultiple('users', 'id', userIds, undefined, 'true', 'true')


        const projectIds = latestActivities
                            .map(({ project_id }) => project_id )
                            .filter((project_id, index, self) => self.indexOf(project_id) === index)
                            .join(',')


        const projectQueryParams = {
          enforce_all_statuses: true,
          enforce_archived:     true,
          filter_by:            'id',
          filter_value:         projectIds
        }

        const projectsPromise = projectApi.getShortList(projectQueryParams)

        const leadIds = join(map(({ lead_id }) => lead_id)(latestActivities))
        const leadsPromise = api
            .getMultiple('leads', 'id', leadIds, undefined, 'true', 'true')
            .catch(reject)

        Promise
          .all([usersPromise, projectsPromise, leadsPromise])
          .then(([users, projects, leads]) => {
            map(latestActivity => {
              // Get user
              if (!isNull(latestActivity.user_id)) {
                  latestActivity.user = find(users, ['id', latestActivity.user_id])
              }

              // Get project
              if (!isNull(latestActivity.project_id)) {
                  latestActivity.project = find(projects, ['id', latestActivity.project_id])
              }

              // Get lead
              if (!isNull(latestActivity.lead_id)) {
                  latestActivity.lead = find(leads, ['id', latestActivity.lead_id])
              }

              return latestActivity
            })(latestActivities)

            resolve(latestActivities)
          })
          .catch(reject)
      })
    },

    // Transform for easier handling and limit
    transform (activities) {
      return flow([
        reject(activity => isUndefined(activity.lead) || isUndefined(activity.project)),
        slice(0, ACTIVITIES_LIMIT),
        map(activity => ({
          at: activity.happened_at,
          activity: activity.name,
          by: cond([
            [activity => isUndefined(activity.user), constant(void 0)],
            [stubTrue, activity => activity.user.display_name]
          ])(activity),

          lead: {
            identifier: activity.lead.id
          },

          person: {
            name: getLeadName(activity.lead, this.$t('ui.dashboard.latest_activity.incomplete_lead')),
            identifier: activity.lead.user_id
          },

          project: {
            name: activity.project.name,
            identifier: activity.project.id
          }
        }))
        ])(activities)
    }
  }
}
</script>

<style lang="sass">
.latest-activity
  h5
    margin-top: 0
    margin-bottom: 0px

  h4
    padding: 0px
    margin-top: 0
    margin-bottom: 10px !important
    text-align: left

  img
    display: inline-block
    padding-top: 4px

  .filter-lists
    display: inline-block
    float: right

  .list
    .activity-item
      padding: 12px 0px
      display: flex
      -webkit-box-flex: 1
      flex-grow: 1
      align-items: flex-start
      -webkit-box-pack: start
      justify-content: flex-start
      max-width: 100%
      width: 100%

      .icon
        box-sizing: border-box
        padding-right: 10px

      .content
        flex-basis: auto
        -webkit-box-flex: 100
        flex-grow: 100
        flex-shrink: 1

      p
        margin-bottom: 0px

        color: $ink-lighter
        span
          color: $ink

      a
        text-decoration: none
        font-size: 14px !important
        line-height: 20px !important
        font-weight: $font-medium
        color: $sensual
        transition: all .15s ease-out
        &:hover
          color: $sensual-dark
        &:active
          color: $sensual-darker

  .empty-state
    h4
      text-align: inherit

    p
      color: $ink-light
</style>



// WEBPACK FOOTER //
// LatestActivityWidget.vue?5aa9acfc